import { Button, Input, Select } from "antd";
import Form, { useForm } from "antd/es/form/Form";
import FormItem from "antd/es/form/FormItem";
import { motion } from "framer-motion";
import { RegisterFormProps } from "@/common/types";
import { Link, useNavigate } from "react-router";
import { useRegisterMutation } from "@/common/api/mutation";
import Typography from "../Typography/Typography";

const user_variants = [
  { value: "customer", label: "Customer" },
  { label: "Vendor", value: "vendor" },
];
const { Option } = Select;

export default function RegisterForm({
  onChangeFunc,
  current,
}: RegisterFormProps & { current: number }) {
  const [form] = useForm();
  const { mutate, isPending } = useRegisterMutation();
  const navigate = useNavigate();

  const onSubmit = (values: any) => {
    mutate(values, {
      onSuccess: () => {
        return navigate("/success", {
          state: { referrer: "success_register" },
        });
      },
    });
  };

  return (
    <Form form={form} onFinish={onSubmit} name="main-register">
      <motion.div className="relative overflow-hidden pb-6">
        {/* Slide 1 */}
        <motion.div
          className={`transition-all duration-500 ${
            current === 1 ? "block opacity-100" : "hidden opacity-0"
          }`}
        >
          <div className="flex flex-col mb-4">
            <Typography type="p">
              <Typography
                mb={1}
                type="span"
                customStyles="inline-block"
                fontFamily="font-kanit"
                fontWeight="font-light"
                textColor="white"
              >
                Choose your account type
              </Typography>
              <Typography
                type="span"
                fontFamily="font-kanit"
                fontWeight="font-light"
                textColor="error"
              >
                *
              </Typography>
            </Typography>
            <FormItem
              name="companyType"
              initialValue={"customer"}
              validateTrigger="onBlur"
            >
              <Select
                style={{ height: 50 }}
                size="large"
                className="font-kanit text-[15px] leading-[22px] font-light text-blackTransparent bg-white rounded-lg"
              >
                {user_variants.map((el) => (
                  <Option
                    className="font-kanit text-[15px] leading-[22px] font-light text-blackTransparent"
                    value={el.value}
                    key={el.value}
                  >
                    {el.label}
                  </Option>
                ))}
              </Select>
            </FormItem>
          </div>
          <div className="flex justify-between items-center gap-5">
            <Button
              disabled={isPending}
              onClick={() => onChangeFunc(2)}
              htmlType="button"
              className="group h-full max-h-[59px] rounded-lg px-16 py-[18px] shadow-none hover:bg-primary"
            >
              <Typography
                type="p"
                textColor="primary"
                fontFamily="font-inter"
                fontWeight="font-semibold"
              >
                Next
              </Typography>
            </Button>
            <Typography
              type="p"
              fontWeight="font-light"
              textColor="white"
              fontFamily="font-kanit"
              className="flex flex-col gap-1"
            >
              Already have an account?
              <Link
                className="text-white font-kanit underline hover:text-gray-light hover:underline font-bold"
                to="/sign-in"
              >
                Login
              </Link>
            </Typography>
          </div>
        </motion.div>

        {/* Slide 2 */}
        <motion.div
          className={`transition-all duration-500 ${
            current === 2 ? "block opacity-100" : "hidden opacity-0"
          }`}
        >
          <FormItem
            validateTrigger="onBlur"
            name="adminFullName"
            className="mb-4"
            rules={[{ required: true, message: "Enter full name" }]}
          >
            <label>
              <Typography
                type="span"
                mb={1}
                customStyles="inline-block"
                fontFamily="font-kanit"
                fontWeight="font-light"
                textColor="white"
              >
                Name
              </Typography>
              <Typography
                type="span"
                fontFamily="font-kanit"
                fontWeight="font-light"
                textColor="error"
              >
                *
              </Typography>
              <Input className="py-3 text-base" placeholder="Name" />
            </label>
          </FormItem>
          <FormItem
            name="adminEmail"
            className="mb-4"
            rules={[
              {
                required: true,
                message: "Enter email",
              },
              {
                pattern:
                  /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                message:
                  "Invalid email address. Email includes an '@' symbol, and valid domain name (e.g., example.com)",
              },
            ]}
            validateTrigger="onBlur"
          >
            <label>
              <Typography
                type="span"
                mb={1}
                customStyles="inline-block"
                fontFamily="font-kanit"
                fontWeight="font-light"
                textColor="white"
              >
                E-mail
              </Typography>
              <Typography
                type="span"
                fontFamily="font-kanit"
                fontWeight="font-light"
                textColor="error"
              >
                *
              </Typography>
              <Input
                type="email"
                className="py-3 text-base"
                placeholder="E-mail"
              />
            </label>
          </FormItem>
          <FormItem
            validateTrigger="onBlur"
            name="adminphoneNumber"
            className="mb-4"
            rules={[
              {
                required: true,
                message: "Enter phone number",
              },
              {
                message: "Enter valid phone number",
                pattern: /^\+?\d{8,}$/,
              },
            ]}
          >
            <label>
              <Typography
                type="span"
                mb={1}
                customStyles="inline-block"
                fontFamily="font-kanit"
                fontWeight="font-light"
                textColor="white"
              >
                Phone number
              </Typography>
              <Typography
                type="span"
                fontFamily="font-kanit"
                fontWeight="font-light"
                textColor="error"
              >
                *
              </Typography>
              <Input
                className="py-3 text-base"
                placeholder="Phone number"
                type="number"
              />
            </label>
          </FormItem>
          <FormItem
            validateTrigger="onBlur"
            name="companyName"
            className="mb-4"
            rules={[{ required: true, message: "Enter company name" }]}
          >
            <label>
              <Typography
                type="span"
                mb={1}
                customStyles="inline-block"
                fontFamily="font-kanit"
                fontWeight="font-light"
                textColor="white"
              >
                Company Name
              </Typography>
              <Typography
                type="span"
                fontFamily="font-kanit"
                fontWeight="font-light"
                textColor="error"
              >
                *
              </Typography>
              <Input className="py-3 text-base" placeholder="Company Name" />
            </label>
          </FormItem>
          <Typography
            type="p"
            lineHeight={22}
            fontSize={15}
            fontWeight="font-light"
            textColor="white"
          >
            By clicking on Sign Up, you agree to our{" "}
            <button
              type="button"
              className="p-0 text-inherit transition underline hover:text-gray-light"
            >
              Terms and Conditions
            </button>
          </Typography>
          <div className="mt-3 flex items-center justify-between gap-3">
            <button
              disabled={isPending}
              onClick={() => onChangeFunc(1)}
              className="hover:border-gray-light h-full max-h-[59px] hover:text-gray-light transition rounded-lg bg-transparent border border-white px-[30px] py-[18px] gap-2 text-base leading-5 text-white flex items-center justify-center"
              type="button"
            >
              Back
            </button>
            <Button
              disabled={isPending}
              htmlType="submit"
              className="group h-full max-h-[59px] rounded-lg px-16 py-[18px] shadow-none hover:bg-primary w-full"
            >
              <Typography
                type="p"
                fontFamily="font-inter"
                fontWeight="font-semibold"
                textColor="primary"
              >
                Sign Up
              </Typography>
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </Form>
  );
}
